import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { Mail } from "lucide-react";
import { useAuthStore } from "../hooks";
import { PATHS } from "@/lib/router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const ForgotPasswordPage = observer(() => {
  const navigate = useNavigate();
  const { t } = useTran();
  const inputsRef = useRef([]);
  const {
    forgotStep,
    forgotEmail,
    forgotOtp,
    loading,
    forgotReset,
    setForgotEmail,
    setForgotOtpAt,
    requestPasswordReset,
    verifyOtp,
  } = useAuthStore();

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    await requestPasswordReset();
  };

  const handleOtpChange = (idx, value) => {
    setForgotOtpAt(idx, value);
    // Focus next input
    const nextRef = inputsRef.current[idx + 1];
    if (nextRef && value) nextRef.focus();
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !forgotOtp[idx] && idx > 0) {
      const prevRef = inputsRef.current[idx - 1];
      if (prevRef) prevRef.focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const success = await verifyOtp();
    if (success) {
      navigate(PATHS.RESET_PASSWORD);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md overflow-hidden rounded-radius bg-card p-8 shadow-2xl">
        {forgotStep === "email" ? (
          <form
            onSubmit={handleEmailSubmit}
            className="flex flex-col items-center"
          >
            {/* Logo */}
            <div className="mb-6 flex justify-center">
              <img
                src="/logos/logo.svg"
                alt="App Logo"
                className="h-16 w-auto"
              />
            </div>

            <h1 className="mb-2 text-2xl font-semibold text-primary">
              {t("Forgot Password")}
            </h1>
            <p className="mb-6 text-center text-sm text-muted-foreground">
              {t("Enter your registered email address to receive a reset code")}
            </p>

            <div className="relative mb-4 w-full">
              <Input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder={t("Email")}
                required
                className="pl-10"
              />
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <Mail className="h-5 w-5" />
              </span>
            </div>

            {forgotReset?.serverError && (
              <p className="mb-4 text-center text-sm text-red-600">
                {forgotReset.serverError}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading || !forgotEmail}
              className="w-full rounded-full bg-primary hover:bg-primary/90"
            >
              {loading ? t("Sending...") : t("Send Reset Code")}
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(PATHS.LOGIN)}
              className="mt-4 w-full"
            >
              {t("Back to Login")}
            </Button>
          </form>
        ) : (
          <form
            onSubmit={handleOtpSubmit}
            className="flex flex-col items-center"
          >
            <h1 className="mb-2 text-2xl font-semibold text-primary">
              {t("Verify Code")}
            </h1>
            <p className="mb-6 text-center text-sm text-muted-foreground">
              {t("Enter the 6-digit code sent to your email")}
            </p>

            <div className="mb-4 flex gap-2">
              {forgotOtp.map((digit, idx) => (
                <Input
                  key={idx}
                  ref={(el) => (inputsRef.current[idx] = el)}
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(idx, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                  className="h-12 w-12 text-center text-lg"
                />
              ))}
            </div>

            {forgotReset?.serverError && (
              <p className="mb-4 text-center text-sm text-red-600">
                {forgotReset.serverError}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading || forgotOtp.some((d) => !d)}
              className="w-full rounded-full bg-primary hover:bg-primary/90"
            >
              {loading ? t("Verifying...") : t("Verify Code")}
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(PATHS.LOGIN)}
              className="mt-4 w-full"
            >
              {t("Back to Login")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
});

export default ForgotPasswordPage;
